import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import {
  Sparkles, Send, FileText, Mail, ScrollText, Activity, Copy, Trash2,
  FileType, FileDown, FileCode, Eraser, FileSignature, X, Eye, Save,
  MailPlus, Briefcase, PenLine, Lock,
} from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "@/components/Markdown";
import WysiwygEditor from "@/components/WysiwygEditor";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isPaidTier } from "@/lib/tiers";
import {
  copyMarkdownAsRichText, downloadBlob, downloadMarkdown, downloadPlainText,
  markdownToCleanHtml,
} from "@/lib/exportDoc";

const MODES = [
  { value: "chat", label: "Chat", icon: Sparkles },
  { value: "analysis", label: "Deep Analysis", icon: Activity },
  { value: "outreach", label: "Outreach Draft", icon: Mail },
  { value: "loi", label: "LOI Draft", icon: ScrollText },
  { value: "term_sheet", label: "Term Sheet", icon: FileText },
];

const TITLE_BY_MODE = {
  chat: "Deal Notes",
  analysis: "Acquisition Analysis",
  outreach: "Outreach Draft",
  loi: "Letter of Intent",
  term_sheet: "Term Sheet",
};

export default function Assistant() {
  const location = useLocation();
  const { user } = useAuth();
  const isPaid = !!user && (["admin", "super_admin"].includes(user.role) ||
    (isPaidTier(user.tier) && user.subscription_status !== "canceled"));
  const initialOid = location.state?.oid || "";
  const [sessionId, setSessionId] = useState(() => `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [oid, setOid] = useState(initialOid);
  const [opportunities, setOpportunities] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [mode, setMode] = useState("chat");
  const [streaming, setStreaming] = useState(false);
  const [partial, setPartial] = useState("");
  const [headerDocOpen, setHeaderDocOpen] = useState(false);
  const [docHandoff, setDocHandoff] = useState(null); // {oid, template_key, title} from preview modal
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/opportunities", { params: { limit: 100 } }).then((r) => setOpportunities(r.data));
  }, []);

  // Detect handoff from the Opportunity Detail "Edit" button on the preview modal.
  // If found in sessionStorage, set the deal context + pop the doc-export drawer
  // and pass the template_key down so it auto-loads.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("acquigine_doc_handoff");
      if (!raw) return;
      const h = JSON.parse(raw);
      // 2-minute freshness window so stale handoffs don't auto-open the drawer
      if (!h?.oid || !h?.template_key || (Date.now() - (h.timestamp || 0)) > 120_000) {
        sessionStorage.removeItem("acquigine_doc_handoff");
        return;
      }
      setOid(h.oid);
      setDocHandoff(h);
      setHeaderDocOpen(true);
      sessionStorage.removeItem("acquigine_doc_handoff");
    } catch {
      sessionStorage.removeItem("acquigine_doc_handoff");
    }
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, partial]);

  const send = async () => {
    if (!input.trim() || streaming) return;
    const userText = input;
    setMessages((m) => [...m, { role: "user", content: userText, mode }]);
    setInput("");
    setStreaming(true);
    setPartial("");
    try {
      const token = localStorage.getItem("ae_token");
      const r = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId, message: userText, opportunity_oid: oid || null, mode }),
      });
      if (!r.ok) throw new Error("AI error");
      const reader = r.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let collected = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop() || "";
        for (const ev of events) {
          if (ev.startsWith("event: done")) continue;
          const dataLines = ev.split("\n").filter((l) => l.startsWith("data: ")).map((l) => l.slice(6));
          const txt = dataLines.join("\n");
          if (txt === "[DONE]") continue;
          collected += txt;
          setPartial(collected);
        }
      }
      setMessages((m) => [...m, { role: "assistant", content: collected, mode }]);
      setPartial("");
    } catch (e) {
      toast.error("AI request failed");
    } finally {
      setStreaming(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send();
  };

  const clearConversation = () => {
    if (!messages.length && !partial) return;
    if (!window.confirm("Are you sure you want to clear the current results?")) return;
    setMessages([]);
    setPartial("");
    setSessionId(`sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    toast.success("Results cleared");
  };

  const selectedOpp = opportunities.find((o) => o.oid === oid);

  if (user && !isPaid) {
    return (
      <div className="max-w-3xl mx-auto py-16 px-4" data-testid="assistant-upgrade-gate">
        <div className="ae-card p-10 text-center space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[10px] uppercase tracking-widest">
            <Lock className="h-3 w-3" /> Pro feature
          </div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-400">Module 10</div>
          <h1 className="font-heading text-4xl tracking-tight text-slate-100">AI Acquisition Assistant</h1>
          <p className="text-sm text-slate-400 max-w-lg mx-auto">
            The AI Acquisition Assistant drafts LOIs, term sheets, outreach, and deep deal
            analysis in seconds ,  powered by Claude and preloaded with your opportunity
            context. It&apos;s available on <span className="text-emerald-300">Pro and Business</span> plans.
          </p>
          <div className="grid sm:grid-cols-2 gap-3 text-left text-xs text-slate-400 max-w-xl mx-auto pt-2">
            <div className="border border-white/10 rounded-sm p-3">
              <div className="text-emerald-300 text-[10px] uppercase tracking-widest mb-1">Deep analysis</div>
              Ownership, motivation, and creative-finance fit scored against your deal.
            </div>
            <div className="border border-white/10 rounded-sm p-3">
              <div className="text-emerald-300 text-[10px] uppercase tracking-widest mb-1">Outreach + LOI</div>
              CAN-SPAM-safe outreach and lender-grade LOIs drafted in your voice.
            </div>
            <div className="border border-white/10 rounded-sm p-3">
              <div className="text-emerald-300 text-[10px] uppercase tracking-widest mb-1">Branded exports</div>
              PDF, DOCX, HTML ,  your brand and signature, never Acquigine&apos;s.
            </div>
            <div className="border border-white/10 rounded-sm p-3">
              <div className="text-emerald-300 text-[10px] uppercase tracking-widest mb-1">Session memory</div>
              Multi-turn deal chats you can revisit and reuse as templates.
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-3 pt-2">
            <Link
              to="/app/billing"
              data-testid="assistant-upgrade-cta"
              className="px-4 py-2 text-xs uppercase tracking-wider rounded-sm border bg-emerald-500/15 text-emerald-200 border-emerald-500/50 hover:bg-emerald-500/25 inline-flex items-center gap-2"
            >
              <Sparkles className="h-3.5 w-3.5" /> Upgrade to unlock
            </Link>
            <Link to="/app/dashboard" className="btn-ghost text-xs">Back to dashboard</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 h-[calc(100vh-160px)] flex flex-col">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Module 10</div>
          <h1 className="font-heading text-4xl tracking-tight">AI Acquisition Assistant</h1>
          <p className="text-sm text-slate-400 mt-1">Powered by Claude · streaming · document-ready output</p>
        </div>
        <div className="flex gap-2 items-center text-sm">
          <select
            data-testid="assistant-opp-select"
            value={oid}
            onChange={(e) => setOid(e.target.value)}
            className="bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2 min-w-[260px]"
          >
            <option value="">No deal context</option>
            {opportunities.map((o) => (
              <option key={o.oid} value={o.oid}>{o.title}</option>
            ))}
          </select>
          <button
            onClick={() => setHeaderDocOpen(true)}
            data-testid="header-generate-document"
            className="btn-primary text-xs inline-flex items-center gap-1.5"
          >
            <FileSignature className="h-3.5 w-3.5" /> Generate Document
          </button>
          <button
            onClick={clearConversation}
            disabled={!messages.length && !partial}
            data-testid="clear-conversation-btn"
            className="btn-ghost text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
          >
            <Eraser className="h-3.5 w-3.5" /> Clear conversation
          </button>
        </div>
      </header>

      {/* Mode tabs */}
      <div className="flex flex-wrap gap-2">
        {MODES.map((m) => {
          const I = m.icon;
          const active = mode === m.value;
          return (
            <button
              key={m.value}
              data-testid={`mode-${m.value}`}
              onClick={() => setMode(m.value)}
              className={`inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-sm border transition-all ${
                active
                  ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/50"
                  : "bg-transparent text-slate-400 border-white/10 hover:bg-white/5 hover:text-slate-200"
              }`}
            >
              <I className="h-3.5 w-3.5" /> {m.label}
            </button>
          );
        })}
      </div>

      {/* Conversation */}
      <div ref={scrollRef} className="flex-1 ae-card p-6 overflow-y-auto space-y-5 min-h-[280px]">
        {messages.length === 0 && !partial && (
          <div className="text-center text-slate-500 py-12">
            <Sparkles className="h-8 w-8 mx-auto text-emerald-500/40" />
            <div className="mt-3 text-sm">{selectedOpp ? `Ask anything about "${selectedOpp.title}"` : "Pick a deal context above or ask anything"}</div>
            <div className="text-xs mt-1">Try: &ldquo;Draft an LOI&rdquo; · &ldquo;What deal structure works?&rdquo; · &ldquo;Estimate ARV&rdquo;</div>
          </div>
        )}
        {messages.map((m, i) => <Bubble key={i} role={m.role} content={m.content} mode={m.mode} />)}
        {partial && <Bubble role="assistant" content={partial} streaming />}
      </div>

      {/* Input */}
      <div className="ae-card p-3 flex gap-2 items-end">
        <textarea
          data-testid="assistant-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          rows={2}
          placeholder={mode === "chat" ? "Ask the AI…" : `Describe what you want for ${mode.replace("_", " ")}…`}
          className="flex-1 bg-transparent resize-none outline-none text-sm placeholder:text-slate-600"
        />
        <button
          onClick={send}
          disabled={streaming || !input.trim()}
          data-testid="assistant-send"
          className="btn-primary text-sm inline-flex items-center gap-2"
        >
          <Send className="h-4 w-4" /> {streaming ? "…" : "Send"}
        </button>
      </div>

      {headerDocOpen && (
        <DocumentGenerator
          oid={oid}
          opportunities={opportunities}
          autoLoadTemplateKey={docHandoff?.template_key || ""}
          content={sanitizeForPreview(
            (messages.filter((m) => m.role === "assistant").slice(-1)[0]?.content) || partial || ""
          )}
          defaultDocType="generic"
          defaultTitle=""
          defaultStem={`document-${new Date().toISOString().slice(0, 10)}`}
          allowEditContent
          onClose={() => { setHeaderDocOpen(false); setDocHandoff(null); }}
        />
      )}
    </div>
  );
}

function Bubble({ role, content, mode, streaming }) {
  const isUser = role === "user";

  if (isUser) {
    return (
      <div className="flex gap-3 flex-row-reverse">
        <div className="h-7 w-7 shrink-0 rounded-sm flex items-center justify-center text-xs font-mono bg-white/8 text-slate-200">U</div>
        <div className="max-w-[80%] text-sm leading-relaxed bg-emerald-500/10 border border-emerald-500/30 rounded-sm px-4 py-3">
          <ReactMarkdown>{content}</ReactMarkdown>
        </div>
      </div>
    );
  }

  // Sanitize assistant output so the result window matches the document export
  // exactly (no em/en dashes, emoji, placeholder lines, AI scaffolding, etc.).
  // The sanitizer preserves structural markdown (headings, tables, lists, HRs).
  const cleaned = sanitizeForPreview(content || "");

  return (
    <div className="flex gap-3">
      <div className="h-7 w-7 shrink-0 rounded-sm flex items-center justify-center text-xs font-mono bg-emerald-500 text-black">
        <Sparkles className="h-3.5 w-3.5" />
      </div>
      <div className="max-w-[80%] flex-1 min-w-0">
        <div className="bg-[#0A0D12] border border-white/8 rounded-sm">
          <div className="px-4 py-3 text-sm leading-relaxed" data-testid="assistant-result">
            <ReactMarkdown>{cleaned}</ReactMarkdown>
            {streaming && <span className="inline-block w-1.5 h-3.5 bg-emerald-400 ml-1 animate-pulse" />}
          </div>
          {!streaming && cleaned && <ResultToolbar content={cleaned} mode={mode} />}
        </div>
      </div>
    </div>
  );
}

function ResultToolbar({ content, mode }) {
  const [busy, setBusy] = useState(null);
  const title = TITLE_BY_MODE[mode] || "Deal Notes";
  // Filename stem retained for future use (copy helper doesn't need it, but the
  // clipboard payload references the current mode via the toast copy).
  void title;

  const copy = async () => {
    setBusy("copy");
    try {
      await copyMarkdownAsRichText(content);
      toast.success("Content copied successfully.");
    } catch {
      toast.error("Copy failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="px-3 py-2 border-t border-white/8 flex flex-wrap gap-1.5 items-center bg-black/20" data-testid="result-toolbar">
      {/* BLOCK 66 Items 1 & 2 ,  in-chat toolbar reduced to Copy only.
          Rationale: Copy is the ONLY action here that isn't duplicated by
          the branded template's final-review screen downstream (PDF, DOCX,
          Markdown, Text, DocuSign, Email all live there). Copy uniquely
          preserves the raw AI-composed analysis text, which is NOT one of
          the 14 fixed templates. The header "Generate Document" button
          (top of page) is now the sole entry into the branded template
          system. */}
      <ToolBtn onClick={copy} icon={Copy} label="Copy" testid="result-copy" busy={busy === "copy"} primary />
    </div>
  );
}

function ToolBtn({ onClick, icon: Icon, label, testid, busy, primary }) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-sm border transition-all ${
        primary
          ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25"
          : "border-white/10 text-slate-400 hover:text-slate-100 hover:bg-white/5"
      } disabled:opacity-50`}
    >
      <Icon className="h-3 w-3" /> {busy ? "…" : label}
    </button>
  );
}


const FORMATS = [
  { key: "pdf",  label: "PDF",                 ext: "pdf"  },
  { key: "docx", label: "Microsoft Word",      ext: "docx" },
  { key: "rtf",  label: "RTF (Pages / Docs)",  ext: "rtf"  },
  { key: "md",   label: "Markdown",            ext: "md"   },
  { key: "txt",  label: "Plain Text",          ext: "txt"  },
];

function DocumentGenerator({ oid: initialOid, opportunities: opportunitiesProp, autoLoadTemplateKey, content: initialContent, defaultDocType, defaultTitle, defaultStem, allowEditContent, onClose }) {
  const [step, setStep] = useState("config"); // config | preview
  const [types, setTypes] = useState([]);
  const [sender, setSender] = useState(null);
  const [brand, setBrand] = useState(null);

  // 14-template mode state (from /api/documents/templates)
  const [docTemplates14, setDocTemplates14] = useState({}); // {Category: [{key,label,description}, ...]}
  const [templateKey, setTemplateKey] = useState("");      // selected template_key (e.g. "loi")
  const [oid, setOid] = useState(initialOid || "");
  const [opps, setOpps] = useState(opportunitiesProp || []);
  const [tplLoading, setTplLoading] = useState(false);
  const [docusignConnected, setDocusignConnected] = useState(false);
  const [brevoConfigured, setBrevoConfigured] = useState(false);
  const [profileReady, setProfileReady] = useState({ ready_for_documents: true });

  const [content, setContent] = useState(initialContent || "");
  const [docType, setDocType] = useState(defaultDocType || "generic");
  const [title, setTitle] = useState(defaultTitle || "");
  const [recipient, setRecipient] = useState("");
  const [stem, setStem] = useState(defaultStem || "document");
  const [format, setFormat] = useState("pdf");
  const [busy, setBusy] = useState(false);

  // Templates
  const [templates, setTemplates] = useState([]);
  const [showSaveTpl, setShowSaveTpl] = useState(false);
  const [tplName, setTplName] = useState("");

  // Delivery
  const [opportunities, setOpportunities] = useState([]);
  const [deliveryMode, setDeliveryMode] = useState(null); // null | "email" | "crm"
  // "Compare with PDF" ,  embeds the actual generated PDF for pixel-parity verification
  const [compareMode, setCompareMode] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfErr, setPdfErr] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [crmOid, setCrmOid] = useState("");
  const [crmNote, setCrmNote] = useState("");

  // Email modal for the 14-template flow
  const [emailFlow, setEmailFlow] = useState(null); // {recipient_email, recipient_name, custom_subject, custom_message}
  // Iter 59 Task 2 ,  DocuSign send confirmation modal
  const [docusignConfirm14, setDocusignConfirm14] = useState(null); // {recipient_email, recipient_name, title, template_key, previewBlobUrl, previewBusy}

  useEffect(() => {
    api.get("/ai/document/types").then((r) => setTypes(r.data)).catch(() => {});
    api.get("/me/sender-profile").then((r) => setSender(r.data)).catch(() => {});
    api.get("/admin/brand").then((r) => setBrand(r.data)).catch(() => {});
    api.get("/ai/document/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/opportunities?limit=200").then((r) => setOpportunities(r.data || [])).catch(() => {});
    // 14-template integration: load templates grouped by category + status checks
    api.get("/documents/templates").then((r) => setDocTemplates14(r.data.grouped || {})).catch(() => {});
    api.get("/docusign/status").then((r) => setDocusignConnected(!!r.data.connected)).catch(() => {});
    api.get("/integrations/me").then((r) => setBrevoConfigured(!!r.data?.brevo?.configured)).catch(() => {});
    api.get("/documents/profile-ready").then((r) => setProfileReady(r.data)).catch(() => {});
    if (!opportunitiesProp || opportunitiesProp.length === 0) {
      api.get("/opportunities?limit=200").then((r) => setOpps(r.data || [])).catch(() => {});
    }
  }, [opportunitiesProp]);

  // When opened via the Opportunity-Detail "Edit" hand-off, auto-load the
  // appropriate template once /api/documents/templates has loaded and the
  // requested oid is set. Guard with a ref-style state so it only fires once.
  const [autoLoaded, setAutoLoaded] = useState(false);
  useEffect(() => {
    if (autoLoaded) return;
    if (!autoLoadTemplateKey || !oid) return;
    if (!docTemplates14 || Object.keys(docTemplates14).length === 0) return;
    setAutoLoaded(true);
    loadTemplate14(autoLoadTemplateKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoadTemplateKey, oid, docTemplates14, autoLoaded]);

  const updateSender = (k, v) => setSender((s) => ({ ...s, [k]: v }));

  // ----- 14-template integration helpers -----
  // Convert template sections [{heading, body}] → markdown text for the WysiwygEditor
  const sectionsToMarkdown = (sections) => {
    if (!Array.isArray(sections)) return "";
    return sections.map((s) => {
      const h = (s.heading || "").trim();
      const b = (s.body || "").toString();
      return h ? `## ${h}\n\n${b}` : b;
    }).join("\n\n");
  };

  // Parse the editor's markdown back into [{heading, body}] for the render endpoint
  const markdownToSections = (md) => {
    if (!md || typeof md !== "string") return [];
    const sections = [];
    let current = { heading: "", body: "" };
    for (const line of md.split("\n")) {
      const h2 = line.match(/^##\s+(.+?)\s*$/);
      const h1 = line.match(/^#\s+(.+?)\s*$/);
      if (h2 || h1) {
        if (current.heading || current.body.trim()) sections.push({ ...current, body: current.body.trim() });
        current = { heading: (h2 ? h2[1] : h1[1]).trim(), body: "" };
      } else {
        current.body += line + "\n";
      }
    }
    if (current.heading || current.body.trim()) sections.push({ ...current, body: current.body.trim() });
    // If user wrote freeform without any headings, wrap as a single section
    if (sections.length === 0 && md.trim()) sections.push({ heading: "Content", body: md.trim() });
    return sections;
  };

  // Load one of the 14 templates for the current opportunity into the editor
  const loadTemplate14 = async (key) => {
    if (!oid) {
      toast.error("Select an opportunity first.");
      return;
    }
    setTplLoading(true);
    setTemplateKey(key);
    try {
      const r = await api.post("/documents/preview", { oid, template_key: key });
      const tpl = r.data || {};
      setTitle(tpl.title || "");
      setStem((tpl.filename_base || `acquigine-${key}`).replace(/[^a-zA-Z0-9_-]+/g, "_"));
      setContent(sectionsToMarkdown(tpl.sections || []));
      toast.success(`Loaded "${tpl.title}" ,  edit, then click Preview`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Could not load template"));
    } finally {
      setTplLoading(false);
    }
  };

  // Render PDF/DOCX via the 14-template endpoint with edits applied
  const render14 = async (fmt) => {
    if (!oid || !templateKey) {
      toast.error("Select an opportunity and a template first.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/documents/render", {
        oid,
        template_key: templateKey,
        format: fmt,
        title_override: title || undefined,
        sections_override: markdownToSections(content),
      }, { responseType: "blob" });
      const ext = fmt === "docx" ? "docx" : "pdf";
      const mime = fmt === "docx"
        ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        : "application/pdf";
      downloadBlob(new Blob([r.data], { type: mime }), `${stem || "document"}.${ext}`);
      toast.success(`${ext.toUpperCase()} downloaded`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Render failed"));
    } finally {
      setBusy(false);
    }
  };

  const sendDocusign14 = async () => {
    if (!oid || !templateKey) { toast.error("Pick an opportunity + template first."); return; }
    // Part C contextual first-use prompt: DocuSign is deferred from onboarding, so prompt to connect here.
    if (!docusignConnected) {
      if (window.confirm("DocuSign isn't connected yet. You connect your own DocuSign account so envelopes send from your identity. Go to Settings → Integrations now to connect?")) {
        window.location.href = "/app/settings";
      }
      return;
    }
    const opp = (opps || opportunities).find((o) => o.oid === oid) || {};
    const recipient_email = opp.owner_email || window.prompt("Recipient email for DocuSign:");
    const recipient_name = opp.owner_name || window.prompt("Recipient name:");
    if (!recipient_email || !recipient_name) return;
    // Iter 59 Task 2 ,  open confirmation modal + render preview instead of sending immediately.
    setDocusignConfirm14({
      recipient_email,
      recipient_name,
      title: title || templateKey,
      template_key: templateKey,
      previewBlobUrl: null,
      previewBusy: true,
    });
    try {
      const r = await api.post("/documents/render",
        { oid, template_key: templateKey, format: "pdf",
          title_override: title || undefined,
          sections_override: markdownToSections(content) },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      setDocusignConfirm14((prev) => prev ? { ...prev, previewBlobUrl: url, previewBusy: false } : prev);
    } catch {
      setDocusignConfirm14((prev) => prev ? { ...prev, previewBusy: false } : prev);
    }
  };

  const confirmDocusignSend14 = async () => {
    if (!docusignConfirm14) return;
    const { recipient_email, recipient_name } = docusignConfirm14;
    setBusy(true);
    try {
      const r = await api.post("/documents/send-via-docusign", {
        oid,
        template_key: templateKey,
        recipient_email,
        recipient_name,
        title_override: title || undefined,
        sections_override: markdownToSections(content),
      });
      toast.success(`DocuSign envelope ${r.data.envelope_id} sent`);
      if (docusignConfirm14.previewBlobUrl) URL.revokeObjectURL(docusignConfirm14.previewBlobUrl);
      setDocusignConfirm14(null);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "DocuSign send failed");
    } finally {
      setBusy(false);
    }
  };

  const cancelDocusignConfirm14 = () => {
    if (docusignConfirm14?.previewBlobUrl) URL.revokeObjectURL(docusignConfirm14.previewBlobUrl);
    setDocusignConfirm14(null);
  };

  const openEmailFlow = () => {
    if (!oid || !templateKey) { toast.error("Pick an opportunity + template first."); return; }
    const opp = (opps || opportunities).find((o) => o.oid === oid) || {};
    setEmailFlow({
      recipient_email: opp.owner_email || "",
      recipient_name: opp.owner_name || "",
      custom_subject: title || "",
      custom_message: "",
    });
  };

  const sendEmail14 = async () => {
    if (!emailFlow) return;
    if (!emailFlow.recipient_email || !emailFlow.recipient_name) {
      toast.error("Recipient email and name required.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/documents/send-via-email", {
        oid,
        template_key: templateKey,
        recipient_email: emailFlow.recipient_email,
        recipient_name: emailFlow.recipient_name,
        custom_subject: emailFlow.custom_subject || undefined,
        custom_message: emailFlow.custom_message || undefined,
        title_override: title || undefined,
        sections_override: markdownToSections(content),
      });
      toast.success(`Email sent to ${r.data.recipient}`);
      setEmailFlow(null);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "brevo_not_connected") {
        toast.error("Connect Brevo in Settings → Integrations.");
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Email failed"));
      }
    } finally {
      setBusy(false);
    }
  };

  // Fetch PDF preview via the 14-template render endpoint with current edits
  const fetchPdf14 = async () => {
    if (!oid || !templateKey) return;
    setPdfBusy(true);
    setPdfErr(null);
    try {
      const r = await api.post("/documents/render", {
        oid,
        template_key: templateKey,
        format: "pdf",
        title_override: title || undefined,
        sections_override: markdownToSections(content),
      }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      setPdfBlobUrl(url);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "PDF preview failed");
      setPdfErr(msg);
      toast.error(msg);
    } finally {
      setPdfBusy(false);
    }
  };


  const generate = async () => {
    if (!sender?.full_name?.trim()) {
      toast.error("Sender full name is required");
      return;
    }
    if (!content?.trim()) {
      toast.error("Document content is required");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(
        `/ai/document/generate?fmt=${format}`,
        {
          content,
          doc_type: docType,
          title: title || null,
          recipient_name: recipient || null,
          filename_stem: stem,
          sender_override: sender,
        },
        { responseType: "blob" },
      );
      const ext = FORMATS.find((f) => f.key === format)?.ext || format;
      downloadBlob(r.data, `${stem}.${ext}`);
      toast.success(`${format.toUpperCase()} downloaded`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Document generation failed");
    } finally {
      setBusy(false);
    }
  };

  const loadTemplate = (tpl) => {
    if (!tpl) return;
    setDocType(tpl.doc_type || "generic");
    setTitle(tpl.title || "");
    setContent(tpl.content || "");
    setRecipient(tpl.recipient_name || "");
    if (tpl.sender_override) {
      setSender((s) => ({ ...s, ...tpl.sender_override }));
    }
    toast.success(`Loaded "${tpl.name}"`);
  };

  const saveTemplate = async () => {
    if (!tplName.trim()) {
      toast.error("Template name required");
      return;
    }
    if (!content?.trim()) {
      toast.error("Add content before saving");
      return;
    }
    try {
      const r = await api.post("/ai/document/templates", {
        name: tplName.trim(),
        doc_type: docType,
        title: title || null,
        content,
        recipient_name: recipient || null,
        sender_override: sender,
      });
      setTemplates((t) => [r.data, ...t]);
      setTplName("");
      setShowSaveTpl(false);
      toast.success("Template saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const deleteTemplate = async (tid) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await api.delete(`/ai/document/templates/${tid}`);
      setTemplates((t) => t.filter((x) => x.tid !== tid));
      toast.success("Template deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const buildDocPayload = () => ({
    content,
    doc_type: docType,
    title: title || null,
    recipient_name: recipient || null,
    filename_stem: stem,
    sender_override: sender,
  });

  // Invalidate the cached PDF blob whenever any field that affects the PDF changes,
  // and clean up the object URL on unmount.
  useEffect(() => {
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    setPdfErr(null);
  }, [content, title, recipient, docType, sender, format]);
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    };
  }, []);

  // Auto-fetch the PDF the moment the user enters the preview step (and any
  // time the cached blob has been invalidated by a content change). Without
  // this, the preview iframe stays stuck on the "Rendering…" spinner forever.
  useEffect(() => {
    if (step !== "preview") return;
    if (pdfBlobUrl || pdfBusy || pdfErr) return;
    if (!content?.trim()) return;
    // 14-template mode if we have a templateKey + oid; otherwise legacy mode
    if (templateKey && oid) fetchPdf14();
    else fetchPdfForCompare();
  }, [step, pdfBlobUrl, pdfBusy, pdfErr, content, templateKey, oid]);

  const fetchPdfForCompare = async () => {
    if (pdfBlobUrl) return; // cached
    if (!content?.trim()) { setPdfErr("Add content first."); return; }
    setPdfBusy(true);
    setPdfErr(null);
    try {
      const r = await api.post("/ai/document/generate?fmt=pdf", buildDocPayload(), { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      setPdfBlobUrl(url);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "PDF generation failed";
      setPdfErr(msg);
      toast.error(msg);
    } finally {
      setPdfBusy(false);
    }
  };

  const toggleCompare = async () => {
    if (compareMode) {
      setCompareMode(false);
      return;
    }
    setCompareMode(true);
    await fetchPdfForCompare();
  };

  const sendEmail = async () => {
    if (!emailTo.trim()) { toast.error("Recipient email required"); return; }
    if (!emailSubject.trim()) { toast.error("Subject required"); return; }
    if (!content?.trim()) { toast.error("Document content is required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/ai/document/send-email", {
        document: buildDocPayload(),
        fmt: format,
        recipient_email: emailTo.trim(),
        subject: emailSubject.trim(),
        message: emailMessage.trim() || null,
      });
      if (r.data?.warning) toast.warning(r.data.warning);
      toast.success(`Sent to ${r.data.to}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email send failed");
    } finally {
      setBusy(false);
    }
  };

  const sendToCrm = async () => {
    if (!crmOid) { toast.error("Pick an opportunity"); return; }
    if (!content?.trim()) { toast.error("Document content is required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/ai/document/send-to-crm", {
        document: buildDocPayload(),
        fmt: format,
        opportunity_oid: crmOid,
        note: crmNote.trim() || null,
      });
      const opp = opportunities.find((o) => o.oid === crmOid);
      toast.success(`Attached to ${opp?.title || "opportunity"}: ${r.data.filename}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Attach failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end items-start" data-testid="doc-generator-overlay">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      {/* BLOCK 66 Item 3 ,  enlarged drawer so both the template picker
          (config step) and the final review / PDF preview step have room
          to render at reading-size on typical desktop displays. Was:
          max-w-5xl (1024px) / max-w-[calc(64rem+96px)] on lg (1120px).
          Now up to 1600px on xl (with a 95vw fallback for smaller screens),
          and taller so the auto-loaded PDF preview isn't scrunched. */}
      <div className="relative bg-[#05070A] border-l border-white/8 w-full max-w-[95vw] xl:max-w-[1600px] h-full max-h-[calc(100vh-32px)] overflow-y-auto" data-testid="doc-generator-drawer">
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-white/8 bg-[#05070A]/95 backdrop-blur">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400">{step === "preview" ? "Final review" : "Generate document"}</div>
            <div className="font-heading text-xl tracking-tight mt-0.5">Branded document export</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-100 p-1.5" data-testid="doc-generator-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {step === "config" && (
          <div className="p-6 space-y-6">
            {/* Templates */}
            <Section title="Templates">
              <div className="flex flex-wrap items-center gap-2">
                <select
                  data-testid="tpl-select"
                  defaultValue=""
                  onChange={(e) => {
                    const tpl = templates.find((t) => t.tid === e.target.value);
                    loadTemplate(tpl);
                    e.target.value = "";
                  }}
                  className="inp flex-1 min-w-[200px]"
                >
                  <option value="" disabled>Load template…</option>
                  {templates.length === 0 && <option value="" disabled>No saved templates yet</option>}
                  {templates.map((t) => (
                    <option key={t.tid} value={t.tid}>{`${t.name} · ${t.doc_type}`}</option>
                  ))}
                </select>
                <button
                  onClick={() => setShowSaveTpl((v) => !v)}
                  data-testid="tpl-save-toggle"
                  className="btn-ghost text-xs inline-flex items-center gap-1.5"
                >
                  <Save className="h-3.5 w-3.5" /> Save as Template
                </button>
              </div>
              {showSaveTpl && (
                <div className="mt-3 flex gap-2 items-center">
                  <input
                    data-testid="tpl-name"
                    value={tplName}
                    onChange={(e) => setTplName(e.target.value)}
                    placeholder="Template name (e.g. Standard LOI Boilerplate)"
                    className="inp flex-1"
                  />
                  <button onClick={saveTemplate} data-testid="tpl-save-confirm" className="btn-primary text-xs">Save</button>
                </div>
              )}
              {templates.length > 0 && (
                <div className="mt-3 space-y-1">
                  {templates.map((t) => (
                    <div key={t.tid} className="flex items-center justify-between text-xs text-slate-400 border-b border-white/5 py-1">
                      <span>{t.name} <span className="text-slate-600">· {t.doc_type}</span></span>
                      <button
                        onClick={() => deleteTemplate(t.tid)}
                        data-testid={`tpl-delete-${t.tid}`}
                        className="text-slate-600 hover:text-rose-400 transition-colors"
                        aria-label="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* Opportunity selector ,  required for 14-template mode */}
            <Section title="Opportunity (required)">
              <select
                data-testid="doc14-opp-select"
                value={oid}
                onChange={(e) => { setOid(e.target.value); setTemplateKey(""); }}
                className="inp"
              >
                <option value="">Select an opportunity…</option>
                {(opps.length ? opps : opportunities).map((o) => (
                  <option key={o.oid} value={o.oid}>{o.title} ,  {o.city}, {o.state}</option>
                ))}
              </select>
              {!oid && (
                <div className="text-[11px] text-amber-400/80 mt-1.5" data-testid="doc14-no-opp-warning">
                  Pick an opportunity above to load one of the 14 branded document templates below.
                </div>
              )}
            </Section>

            {/* Doc type ,  14 templates from the Document Generator (Opportunity-detail page) */}
            <Section title="Document type">
              {Object.keys(docTemplates14).length === 0 ? (
                <div className="text-[11px] text-slate-500" data-testid="doc14-loading">Loading templates…</div>
              ) : (
                <div className="space-y-4" data-testid="doc14-type-grid">
                  {Object.entries(docTemplates14).map(([category, list]) => (
                    <div key={category}>
                      <div className="text-[10px] uppercase tracking-widest text-emerald-400/80 mb-1.5">{category}</div>
                      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                        {list.map((t) => (
                          <button
                            key={t.key}
                            onClick={() => loadTemplate14(t.key)}
                            disabled={!oid || tplLoading}
                            data-testid={`doc14-type-${t.key}`}
                            className={`text-left text-xs px-3 py-2.5 rounded-sm border transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
                              templateKey === t.key
                                ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300"
                                : "border-white/8 text-slate-300 hover:text-slate-100 hover:border-white/20"
                            }`}
                          >
                            <div className="font-semibold">{t.label}</div>
                            <div className="text-[10px] text-slate-500 mt-0.5 line-clamp-2">{t.description}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* Legacy generic doc-type grid kept hidden ,  replaced by the 14-template grid above */}
            {false && (
            <Section title="Document type">
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2" data-testid="doc-type-grid">
                {types.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setDocType(t.key)}
                    data-testid={`doc-type-${t.key}`}
                    className={`text-left text-xs px-3 py-2.5 rounded-sm border transition-colors ${
                      docType === t.key
                        ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300"
                        : "border-white/8 text-slate-400 hover:text-slate-100 hover:border-white/20"
                    }`}
                  >
                    <div className="font-semibold">{t.title}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">{t.category}</div>
                  </button>
                ))}
              </div>
            </Section>
            )}

            {allowEditContent && (
              <Section title="Document content">
                <p className="text-[11px] text-slate-500 -mt-2 mb-2">
                  Visual editor. Use the toolbar for headings, lists, tables, quotes, and links. The exported PDF mirrors this formatting exactly.
                </p>
                <WysiwygEditor
                  value={content}
                  onChange={setContent}
                  placeholder="Paste your AI-generated draft, or write your own content here."
                />
                <div className="text-[10px] text-slate-600 mt-1" data-testid="doc-content-stats">
                  {content.length.toLocaleString()} chars · {content.split(/\s+/).filter(Boolean).length.toLocaleString()} words
                </div>
                {/* Hidden mirror so tests / form submissions can still read the raw markdown */}
                <textarea
                  data-testid="doc-content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="sr-only"
                  tabIndex={-1}
                  aria-hidden="true"
                />
              </Section>
            )}

            <Section title="Document details">
              <div className="grid grid-cols-2 gap-3">
                <Field2 label="Title">
                  <input data-testid="doc-title" value={title} onChange={(e) => setTitle(e.target.value)} className="inp" placeholder="LOI for Cleveland Multifamily" />
                </Field2>
                <Field2 label="Recipient name (optional)">
                  <input data-testid="doc-recipient" value={recipient} onChange={(e) => setRecipient(e.target.value)} className="inp" placeholder="[Recipient name]" />
                </Field2>
                <Field2 label="Filename">
                  <input data-testid="doc-stem" value={stem} onChange={(e) => setStem(e.target.value)} className="inp font-mono text-xs" />
                </Field2>
                <Field2 label="Format">
                  <select data-testid="doc-format" value={format} onChange={(e) => setFormat(e.target.value)} className="inp">
                    {FORMATS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                  </select>
                </Field2>
              </div>
            </Section>

            <Section title="Sender block (auto-applied to signature)">
              <p className="text-[11px] text-slate-500 -mt-2 mb-3">Edit inline for this document, or update your default in Settings.</p>
              <div className="grid grid-cols-2 gap-3">
                <Field2 label="Full name *">
                  <input data-testid="sndr-name" value={sender?.full_name || ""} onChange={(e) => updateSender("full_name", e.target.value)} className="inp" />
                </Field2>
                <Field2 label="Title">
                  <input data-testid="sndr-title" value={sender?.title || ""} onChange={(e) => updateSender("title", e.target.value)} className="inp" placeholder="Managing Partner" />
                </Field2>
                <Field2 label="Company">
                  <input data-testid="sndr-company" value={sender?.company_name || ""} onChange={(e) => updateSender("company_name", e.target.value)} className="inp" />
                </Field2>
                <Field2 label="Phone">
                  <input data-testid="sndr-phone" value={sender?.phone || ""} onChange={(e) => updateSender("phone", e.target.value)} className="inp" />
                </Field2>
                <Field2 label="Email">
                  <input data-testid="sndr-email" value={sender?.email || ""} onChange={(e) => updateSender("email", e.target.value)} className="inp" />
                </Field2>
                <Field2 label="License (optional)">
                  <input data-testid="sndr-license" value={sender?.license_number || ""} onChange={(e) => updateSender("license_number", e.target.value)} className="inp" />
                </Field2>
              </div>
            </Section>

            <div className="flex gap-2 justify-end pt-4 border-t border-white/8">
              <button onClick={onClose} className="btn-ghost text-sm" data-testid="doc-cancel">Cancel</button>
              <button onClick={() => setStep("preview")} className="btn-primary text-sm inline-flex items-center gap-1.5" data-testid="doc-preview-export">
                <Eye className="h-3.5 w-3.5" /> Preview &amp; Export PDF
              </button>
            </div>
          </div>
        )}

        {step === "preview" && (
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs text-slate-400">
                Final review: executive-ready preview matching the exported {format.toUpperCase()} exactly, including branded header/footer and page numbers.
              </div>
            </div>

            {/* Auto-loaded PDF preview, single source of truth, matches the exported file exactly. */}
            <div className="space-y-2">
              {pdfErr ? (
                <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm p-4 rounded-sm" data-testid="preview-pdf-error">
                  {pdfErr}
                  <button onClick={fetchPdfForCompare} className="ml-2 underline hover:text-red-200" data-testid="preview-pdf-retry">
                    Retry
                  </button>
                </div>
              ) : pdfBusy || !pdfBlobUrl ? (
                <div className="bg-white/5 border border-white/10 text-slate-300 text-sm p-8 rounded-sm text-center" data-testid="preview-pdf-loading">
                  Rendering the executive-ready PDF…
                </div>
              ) : (
                <iframe
                  src={pdfBlobUrl}
                  title="Document preview"
                  data-testid="preview-pdf-iframe"
                  className="w-full bg-white shadow-2xl border border-white/10 rounded-sm"
                  // BLOCK 66 follow-up ,  enlarged in-modal PDF viewer so a
                  // full Letter page renders comfortably alongside the widened
                  // drawer. Was maxWidth 780px / height 1100px. Now scales to
                  // the drawer width up to 1200px (roughly 8.5in at 141dpi)
                  // and fills the available viewport height so the whole
                  // cover page fits without pinching.
                  style={{ height: "calc(100vh - 220px)", minHeight: "1200px", maxWidth: "1200px", margin: "0 auto", display: "block" }}
                />
              )}
              <div className="text-[11px] text-slate-500 text-center">
                This is the exact {format.toUpperCase()} that will be delivered.
              </div>
            </div>

            {false && (
            (() => {
              const cleanContent = sanitizeForPreview(content);
              const cleanTitle = sanitizeForPreview(title);
              const cleanRecipient = sanitizeForPreview(recipient);
              const typeMeta = types.find((t) => t.key === docType);
              const typeTitle = typeMeta?.title || "Document";
              const effectiveTitle = cleanTitle || typeTitle;
              const hasCover = COVER_TYPES.has(docType);
              const needsSignature = ["transaction", "communication", "presentation"].includes(typeMeta?.category);
              const today = new Date().toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
              const brandContact = [brand?.website, brand?.phone, brand?.email].filter(Boolean).join("  ·  ");
              const senderSubLines = [
                sender?.title,
                sender?.company_name || brand?.brand_name,
                [sender?.phone, sender?.email].filter(Boolean).join("  ·  "),
              ].filter(Boolean);

              // Page wrapper styled to letter proportions (8.5 × 11), 1" margins
              const pageStyle = {
                width: "100%",
                maxWidth: "780px",
                aspectRatio: "8.5 / 11",
                margin: "0 auto",
              };

              return (
                <div className="space-y-6" data-testid="doc-preview-document">
                  {/* COVER PAGE */}
                  {hasCover && (
                    <div
                      style={pageStyle}
                      className="bg-white text-[#101010] shadow-2xl border border-white/10 rounded-sm relative overflow-hidden"
                      data-testid="doc-preview-cover"
                    >
                      <div className="absolute inset-0 flex flex-col items-center px-12 py-16">
                        {/* Top: logo + brand + tagline */}
                        <div className="flex flex-col items-center" style={{ marginTop: "8%" }}>
                          {brand?.logo_url ? (
                            <img src={brand.logo_url} alt="" style={{ height: "200px", maxWidth: "65%", objectFit: "contain" }} />
                          ) : (
                            <div className="font-bold text-4xl text-[#0A2B6B]">{brand?.brand_name || "Acquigine"}</div>
                          )}
                          <div className="mt-4 font-bold text-[#0A2B6B]" style={{ fontSize: "20px", letterSpacing: "0.5px" }}>
                            {brand?.brand_name || "Acquigine"}
                          </div>
                          {brand?.tagline && (
                            <div className="italic text-[#5A5A5A] mt-1" style={{ fontSize: "11px" }}>
                              {brand.tagline}
                            </div>
                          )}
                        </div>

                        {/* Middle: eyebrow + title + date + separator */}
                        <div className="flex flex-col items-center text-center" style={{ marginTop: "12%" }}>
                          <div className="font-bold uppercase text-[#0A2B6B]" style={{ fontSize: "12px", letterSpacing: "3px" }}>
                            {typeTitle}
                          </div>
                          <div className="font-bold mt-3 text-[#101010]" style={{ fontSize: "32px", lineHeight: 1.15, maxWidth: "70%" }}>
                            {effectiveTitle}
                          </div>
                          <div className="italic text-[#5A5A5A] mt-3" style={{ fontSize: "13px" }}>
                            {today}
                          </div>
                          <div className="mt-5 h-px bg-[#0A2B6B]" style={{ width: "120px" }} />
                        </div>

                        {/* Bottom: PREPARED FOR / PREPARED BY */}
                        <div className="flex flex-col items-center text-center mt-auto" style={{ marginBottom: "6%" }}>
                          {cleanRecipient && (
                            <>
                              <div className="font-bold uppercase text-[#777]" style={{ fontSize: "10px", letterSpacing: "2px" }}>
                                Prepared for
                              </div>
                              <div className="font-bold text-[#101010] mt-1" style={{ fontSize: "15px" }}>
                                {cleanRecipient}
                              </div>
                              <div className="h-6" />
                            </>
                          )}
                          <div className="font-bold uppercase text-[#777]" style={{ fontSize: "10px", letterSpacing: "2px" }}>
                            Prepared by
                          </div>
                          <div className="font-bold text-[#101010] mt-1" style={{ fontSize: "15px" }}>
                            {sender?.full_name || brand?.brand_name || "Acquigine"}
                          </div>
                          {senderSubLines.map((line, idx) => (
                            <div key={idx} className="text-[#5A5A5A]" style={{ fontSize: "11px", marginTop: idx === 0 ? "4px" : "2px" }}>
                              {line}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Confidentiality at very bottom */}
                      {brand?.confidentiality_notice && (
                        <div className="absolute left-0 right-0 bottom-4 px-12 text-center italic text-[#999]" style={{ fontSize: "8px", lineHeight: 1.4 }}>
                          {brand.confidentiality_notice}
                        </div>
                      )}
                    </div>
                  )}

                  {/* CONTENT PAGES (paginated) */}
                  <PaginatedContentPages
                    cleanContent={cleanContent}
                    brand={brand}
                    sender={sender}
                    hasCover={hasCover}
                    needsSignature={needsSignature}
                    typeTitle={typeTitle}
                    effectiveTitle={effectiveTitle}
                    today={today}
                    cleanRecipient={cleanRecipient}
                    brandContact={brandContact}
                    pageStyle={pageStyle}
                  />
                </div>
              );
            })()
            )}

            <div className="flex flex-wrap gap-2 justify-end pt-4 border-t border-white/8">
              <button onClick={() => setStep("config")} className="btn-ghost text-sm inline-flex items-center gap-1.5" data-testid="doc-back">
                <X className="h-3.5 w-3.5" /> Back to edit
              </button>
              <button
                onClick={() => render14("pdf")}
                disabled={busy || !templateKey || !oid}
                data-testid="doc14-action-pdf"
                className="btn-ghost text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <FileDown className="h-3.5 w-3.5" /> PDF
              </button>
              <button
                onClick={() => render14("docx")}
                disabled={busy || !templateKey || !oid}
                data-testid="doc14-action-docx"
                className="btn-ghost text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <FileType className="h-3.5 w-3.5" /> DOCX
              </button>
              <button
                onClick={sendDocusign14}
                disabled={busy || !templateKey || !oid}
                data-testid="doc14-action-docusign"
                title={!docusignConnected ? "DocuSign not connected ,  click to connect in Settings" : ""}
                className="px-3 py-1.5 text-sm rounded-sm border bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25 disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                <PenLine className="h-3.5 w-3.5" /> Send via DocuSign
              </button>
              <button
                onClick={openEmailFlow}
                disabled={busy || !templateKey || !oid}
                data-testid="doc14-action-email"
                className="px-3 py-1.5 text-sm rounded-sm border bg-cyan-500/15 text-cyan-200 border-cyan-500/50 hover:bg-cyan-500/25 disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                <MailPlus className="h-3.5 w-3.5" /> Send via Email
              </button>
            </div>

            {/* Email flow modal (14-template) */}
            {emailFlow && (
              <div className="ae-card p-4 mt-3 border-cyan-500/30 space-y-3" data-testid="doc14-email-flow">
                <div className="text-[10px] uppercase tracking-widest text-cyan-400">Send via Email</div>
                {!brevoConfigured && (
                  <div className="text-[11px] text-amber-300 border border-amber-500/40 bg-amber-500/10 p-2 rounded-sm">
                    Your Brevo account is not connected ,  using platform fallback. <Link to="/app/settings" className="underline">Connect in Settings →</Link>
                  </div>
                )}
                <Field2 label="Recipient name">
                  <input value={emailFlow.recipient_name}
                         onChange={(e) => setEmailFlow({ ...emailFlow, recipient_name: e.target.value })}
                         data-testid="doc14-email-recipient-name" className="inp" />
                </Field2>
                <Field2 label="Recipient email">
                  <input type="email" value={emailFlow.recipient_email}
                         onChange={(e) => setEmailFlow({ ...emailFlow, recipient_email: e.target.value })}
                         data-testid="doc14-email-recipient-email" className="inp" />
                </Field2>
                <Field2 label="Subject">
                  <input value={emailFlow.custom_subject}
                         onChange={(e) => setEmailFlow({ ...emailFlow, custom_subject: e.target.value })}
                         data-testid="doc14-email-subject" className="inp" />
                </Field2>
                <Field2 label="Cover message (optional, HTML allowed)">
                  <textarea rows={3} value={emailFlow.custom_message}
                            onChange={(e) => setEmailFlow({ ...emailFlow, custom_message: e.target.value })}
                            data-testid="doc14-email-message" className="inp" />
                </Field2>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setEmailFlow(null)} className="btn-ghost text-xs">Cancel</button>
                  <button onClick={sendEmail14} disabled={busy} data-testid="doc14-email-send"
                          className="btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-50">
                    <MailPlus className="h-3 w-3" /> {busy ? "Sending…" : "Send PDF"}
                  </button>
                </div>
              </div>
            )}

            {/* Iter 59 Task 2 ,  DocuSign send confirmation modal (14-template flow) */}
            {docusignConfirm14 && (
              <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="docusign14-confirm-modal">
                <div className="ae-card p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-1">Confirm DocuSign send</div>
                      <div className="font-heading text-xl text-slate-100">Review before dispatching</div>
                      <p className="text-xs text-slate-400 mt-1">
                        This sends a signable envelope. Once sent, DocuSign notifies the recipient immediately &mdash; this action cannot be undone.
                      </p>
                    </div>
                    <button onClick={cancelDocusignConfirm14} className="text-slate-400 hover:text-slate-100" data-testid="docusign14-confirm-close">✕</button>
                  </div>
                  <div className="grid grid-cols-2 gap-3 border border-white/10 rounded-sm p-3 bg-slate-800/40 text-xs mb-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">Recipient name</div>
                      <div className="font-mono text-slate-100 mt-1" data-testid="docusign14-confirm-recipient-name">{docusignConfirm14.recipient_name}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">Recipient email</div>
                      <div className="font-mono text-slate-100 mt-1" data-testid="docusign14-confirm-recipient-email">{docusignConfirm14.recipient_email}</div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">Document</div>
                      <div className="text-slate-100 mt-1" data-testid="docusign14-confirm-title">{docusignConfirm14.title}</div>
                    </div>
                  </div>
                  <div className="border border-white/10 rounded-sm bg-black/40 mb-4 overflow-hidden" style={{ height: "360px" }}>
                    {docusignConfirm14.previewBusy || !docusignConfirm14.previewBlobUrl ? (
                      <div className="h-full flex items-center justify-center text-xs text-slate-500">Rendering preview...</div>
                    ) : (
                      <iframe title="DocuSign document preview" src={docusignConfirm14.previewBlobUrl} className="w-full h-full" data-testid="docusign14-confirm-preview" />
                    )}
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={cancelDocusignConfirm14} className="px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border border-white/10 text-slate-300 hover:bg-white/5" data-testid="docusign14-confirm-cancel">Cancel</button>
                    <button onClick={confirmDocusignSend14} disabled={busy} className="px-4 py-1.5 text-xs uppercase tracking-wider rounded-sm border bg-emerald-500/15 text-emerald-200 border-emerald-500/50 hover:bg-emerald-500/25 disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="docusign14-confirm-send-now">
                      {busy ? "Sending…" : "Send Now"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {false && (
            <div className="flex flex-wrap gap-2 justify-end pt-4 border-t border-white/8">
              <button onClick={() => setStep("config")} className="btn-ghost text-sm" data-testid="doc-back-legacy">Back to edit</button>
              <button
                onClick={() => { setDeliveryMode("crm"); }}
                disabled={busy}
                className="btn-ghost text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid="open-crm-attach"
              >
                <Briefcase className="h-3.5 w-3.5" /> Send via CRM
              </button>
              <button
                onClick={() => { setDeliveryMode("email"); }}
                disabled={busy}
                className="btn-ghost text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid="open-send-email"
              >
                <MailPlus className="h-3.5 w-3.5" /> Send via Email
              </button>
              <button
                type="button"
                onClick={() => toast("E-signature delivery is coming soon. We'll notify you when it launches.", { description: "DocuSign integration is on the roadmap." })}
                className="btn-ghost text-sm inline-flex items-center gap-1.5 opacity-70 cursor-not-allowed"
                data-testid="open-send-esign"
                aria-disabled="true"
                title="E-signatures coming soon"
              >
                <PenLine className="h-3.5 w-3.5" /> Send for e-Signature
                <span className="ml-1 rounded-full bg-emerald-500/10 text-emerald-300 text-[9px] uppercase tracking-wider px-1.5 py-[1px] border border-emerald-500/30">Soon</span>
              </button>
              <button onClick={generate} disabled={busy} className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="doc-approve-generate">
                <FileDown className="h-3.5 w-3.5" /> {busy ? "Generating…" : `Approve & Download ${format.toUpperCase()}`}
              </button>
            </div>
            )}

            {deliveryMode === "email" && (
              <div className="ae-card p-5 space-y-3 border-emerald-500/30" data-testid="email-panel">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-400">Send via Email</div>
                  <button onClick={() => setDeliveryMode(null)} className="text-slate-500 hover:text-slate-200 text-xs">close</button>
                </div>
                <Field2 label="Recipient email">
                  <input data-testid="email-to" type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="seller@example.com" className="inp" />
                </Field2>
                <Field2 label="Subject">
                  <input data-testid="email-subject" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} placeholder={title || "Acquigine document"} className="inp" />
                </Field2>
                <Field2 label="Message (optional, HTML allowed)">
                  <textarea data-testid="email-message" rows={3} value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)} placeholder="A short cover note for the recipient…" className="inp text-xs" />
                </Field2>
                <div className="text-[10px] text-slate-500 leading-relaxed">
                  Document will be generated as a {format.toUpperCase()} and attached to the email. In Resend test mode you can only send to the API key owner&apos;s verified address.
                </div>
                <button onClick={sendEmail} disabled={busy} data-testid="email-send-btn" className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50 w-full justify-center">
                  <MailPlus className="h-3.5 w-3.5" /> {busy ? "Sending…" : `Send ${format.toUpperCase()} via Email`}
                </button>
              </div>
            )}

            {deliveryMode === "crm" && (
              <div className="ae-card p-5 space-y-3 border-emerald-500/30" data-testid="crm-panel">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-400">Attach to Opportunity</div>
                  <button onClick={() => setDeliveryMode(null)} className="text-slate-500 hover:text-slate-200 text-xs">close</button>
                </div>
                <Field2 label="Opportunity">
                  <select data-testid="crm-opp" value={crmOid} onChange={(e) => setCrmOid(e.target.value)} className="inp">
                    <option value="">Select an opportunity…</option>
                    {opportunities.map((o) => (
                      <option key={o.oid} value={o.oid}>{`${o.title} (${o.type})`}</option>
                    ))}
                  </select>
                </Field2>
                <Field2 label="Note (optional)">
                  <input data-testid="crm-note" value={crmNote} onChange={(e) => setCrmNote(e.target.value)} placeholder="Initial outreach LOI draft" className="inp" />
                </Field2>
                <div className="text-[10px] text-slate-500">
                  Generated {format.toUpperCase()} will be saved to the opportunity&apos;s attachments and recorded in the audit log.
                </div>
                <button onClick={sendToCrm} disabled={busy || !crmOid} data-testid="crm-send-btn" className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-50 w-full justify-center">
                  <Briefcase className="h-3.5 w-3.5" /> {busy ? "Attaching…" : "Attach to Opportunity"}
                </button>
              </div>
            )}
          </div>
        )}

        <style>{`
          .inp { width: 100%; background: #0A0D12; border: 1px solid rgba(255,255,255,0.1); border-radius: 4px; padding: 0.45rem 0.6rem; font-size: 0.85rem; outline: none; color: #F8FAFC; }
          .inp:focus { border-color: rgba(16,185,129,0.6); }
          .prose-doc h1 { font-size: 17px; font-weight: 700; color: #0A2B6B; margin: 12px 0 6px; }
          .prose-doc h2 { font-size: 14px; font-weight: 700; color: #0A2B6B; margin: 10px 0 4px; }
          .prose-doc h3 { font-size: 12px; font-weight: 700; color: #101010; margin: 8px 0 3px; }
          .prose-doc p { font-size: 11.5px; line-height: 1.55; margin: 4px 0; color: #101010; }
          .prose-doc ul, .prose-doc ol { font-size: 11.5px; margin: 4px 0; padding-left: 22px; color: #101010; }
          .prose-doc li { margin: 2px 0; }
          .prose-doc table { border-collapse: collapse; font-size: 10.5px; width: 100%; margin: 6px 0; }
          .prose-doc th { background: #0A2B6B; color: white; padding: 6px; text-align: left; }
          .prose-doc td { padding: 6px; border: 1px solid #E2E8F0; }
        `}</style>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-3">{title}</div>
      {children}
    </div>
  );
}

function Field2({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

// =====================================================================
// Multi-page paginated preview
// =====================================================================

// Per-page content-area capacity (px). Letter aspect at 780px width = 1009px tall.
// We subtract fixed header (~130px full / ~70px simplified), eyebrow+title (~75px),
// signature (~140px), footer (~50px), and internal padding (~32px).
const PAGE_HEIGHT = 1009;
const FOOTER_PADDING = 50 + 32; // footer + body padding
const FULL_HEADER = 130;
const SIMPLE_HEADER = 70;
const EYEBROW_TITLE = 75;
const SIGNATURE_BLOCK = 140;

function _capacityFor({ isFirst, isLast, hasCover, needsSignature }) {
  // Pages 1+ always have a branded header ,  first content page has the FULL header,
  // subsequent pages have a simplified header (matches backend PDF behavior).
  const header = isFirst ? FULL_HEADER : SIMPLE_HEADER;
  const eyebrow = isFirst && !hasCover ? EYEBROW_TITLE : 0;
  const signature = isLast && needsSignature ? SIGNATURE_BLOCK : 0;
  return PAGE_HEIGHT - header - eyebrow - signature - FOOTER_PADDING;
}

function PaginatedContentPages(props) {
  const {
    cleanContent, brand, sender, hasCover, needsSignature,
    typeTitle, effectiveTitle, today, cleanRecipient, brandContact, pageStyle,
  } = props;

  const fullHtml = useMemo(() => markdownToCleanHtml(cleanContent), [cleanContent]);
  const [pages, setPages] = useState(null);
  const measureRef = useRef(null);

  useLayoutEffect(() => {
    if (!measureRef.current) return;
    const children = Array.from(measureRef.current.children);
    if (children.length === 0) {
      setPages([""]);
      return;
    }

    const heights = children.map((c) => c.getBoundingClientRect().height + 6); // +margin

    // Greedy pagination: walk children, start a new page when cumulative height
    // exceeds the capacity for that page index. We iterate twice if needed because
    // first/last page capacities depend on whether there are >1 pages.
    const buildGroups = (childCount) => {
      const groups = [];
      let cur = [];
      let used = 0;
      let pageIdx = 0;
      for (let i = 0; i < children.length; i++) {
        // Snapshot: are we likely the last page? Optimistically YES on first pass.
        const isLast = (pageIdx === (groups.length + (cur.length > 0 ? 1 : 1) - 1)); // unknown ,  assume yes
        const cap = _capacityFor({
          isFirst: pageIdx === 0,
          isLast,
          hasCover,
          needsSignature,
        });
        if (used + heights[i] > cap && cur.length > 0) {
          groups.push(cur);
          cur = [];
          used = 0;
          pageIdx += 1;
        }
        cur.push(i);
        used += heights[i];
      }
      if (cur.length > 0) groups.push(cur);
      return groups;
    };

    // Pass 1: build assuming last-page rules everywhere
    let groups = buildGroups();

    // Pass 2: if we have >1 group, re-paginate so middle pages get full capacity
    // (no signature deduction) and only the last page gets the signature deduction.
    if (groups.length > 1) {
      groups = [];
      let cur = [];
      let used = 0;
      let pageIdx = 0;
      const totalChildren = children.length;
      // We don't know total page count in advance; iterate, and at each potential
      // overflow, decide based on whether this would be the last page (no more
      // children remaining).
      for (let i = 0; i < totalChildren; i++) {
        const remaining = totalChildren - i;
        const isLast = remaining === 1; // tentative
        const cap = _capacityFor({
          isFirst: pageIdx === 0,
          isLast,
          hasCover,
          needsSignature,
        });
        if (used + heights[i] > cap && cur.length > 0) {
          groups.push(cur);
          cur = [];
          used = 0;
          pageIdx += 1;
        }
        cur.push(i);
        used += heights[i];
      }
      if (cur.length > 0) groups.push(cur);
    }

    const htmlPages = groups.map((grp) => grp.map((i) => children[i].outerHTML).join(""));
    setPages(htmlPages.length ? htmlPages : [""]);
  }, [fullHtml, hasCover, needsSignature]);

  const pageCount = pages ? pages.length : 1;

  return (
    <>
      {/* Hidden measurement container ,  uses exact body column width + typography
          so the measured heights match the rendered content. */}
      <div
        ref={measureRef}
        className="prose-doc"
        style={{
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          width: "668px",
          fontSize: "12px",
          lineHeight: 1.5,
          left: 0,
          top: 0,
        }}
        dangerouslySetInnerHTML={{ __html: fullHtml }}
      />

      {(pages || [fullHtml]).map((html, idx) => {
        const isFirst = idx === 0;
        const isLast = idx === pageCount - 1;
        return (
          <ContentPageCard
            key={idx}
            html={html}
            isFirst={isFirst}
            isLast={isLast}
            pageNumber={(hasCover ? 2 : 1) + idx}
            totalPages={pageCount + (hasCover ? 1 : 0)}
            brand={brand}
            sender={sender}
            hasCover={hasCover}
            needsSignature={needsSignature}
            typeTitle={typeTitle}
            effectiveTitle={effectiveTitle}
            today={today}
            cleanRecipient={cleanRecipient}
            brandContact={brandContact}
            pageStyle={pageStyle}
          />
        );
      })}
    </>
  );
}

function ContentPageCard(props) {
  const {
    html, isFirst, isLast, pageNumber, totalPages, brand, sender, hasCover,
    needsSignature, typeTitle, effectiveTitle, today, cleanRecipient,
    brandContact, pageStyle,
  } = props;

  const cardTestId = `doc-preview-content-page-${pageNumber}`;

  return (
    <div
      style={pageStyle}
      className="bg-white text-[#101010] shadow-2xl border border-white/10 rounded-sm overflow-hidden flex flex-col"
      data-testid={cardTestId}
      data-page-number={pageNumber}
    >
      {/* Back-compat marker for tests that query the first content card */}
      {isFirst && <span data-testid="doc-preview-content" style={{ display: "none" }} aria-hidden="true" />}
      {/* Branded header ,  full on first content page, simplified on subsequent */}
      {isFirst ? (
        <div className="flex items-start justify-between border-b-2 border-[#0A2B6B]/80" style={{ padding: "28px 56px 12px" }}>
          <div className="flex items-center gap-3">
            {brand?.logo_url && <img src={brand.logo_url} alt="" style={{ height: "80px", width: "auto", objectFit: "contain" }} />}
          </div>
          <div className="text-right">
            <div className="font-bold text-[#0A2B6B]" style={{ fontSize: "15px" }}>{brand?.brand_name || "Acquigine"}</div>
            {brand?.tagline && <div className="italic text-[#5A5A5A]" style={{ fontSize: "10px" }}>{brand.tagline}</div>}
            {brandContact && (
              <div className="text-[#5A5A5A] mt-0.5" style={{ fontSize: "9.5px" }}>{brandContact}</div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between border-b border-[#CBD5E1]" style={{ padding: "16px 56px 8px" }}>
          <div className="flex items-center gap-3">
            {brand?.logo_url && <img src={brand.logo_url} alt="" style={{ height: "36px", width: "auto", objectFit: "contain" }} />}
            <div className="font-bold text-[#0A2B6B]" style={{ fontSize: "11px" }}>
              {effectiveTitle || brand?.brand_name || "Acquigine"}
            </div>
          </div>
          {brand?.confidentiality_notice && (
            <div className="italic text-[#777]" style={{ fontSize: "8.5px", letterSpacing: "0.5px" }}>CONFIDENTIAL</div>
          )}
        </div>
      )}

      {/* Eyebrow + title (only on first content page when there's no cover) */}
      {isFirst && !hasCover && (
        <div style={{ padding: "20px 56px 4px" }}>
          <div className="font-bold uppercase text-[#0A2B6B]" style={{ fontSize: "11px", letterSpacing: "2.5px" }}>
            {typeTitle}
          </div>
          <div className="font-bold mt-1 text-[#101010]" style={{ fontSize: "26px", lineHeight: 1.2 }}>{effectiveTitle}</div>
          <div className="italic text-[#5A5A5A] mt-1" style={{ fontSize: "12px" }}>
            {today}{cleanRecipient ? `  ·  Prepared for ${cleanRecipient}` : ""}
          </div>
        </div>
      )}

      {/* Body slice */}
      <div
        className="prose-doc flex-1"
        style={{ padding: "16px 56px 8px", fontSize: "12px", lineHeight: 1.5, overflow: "hidden" }}
        dangerouslySetInnerHTML={{ __html: html }}
      />

      {/* Signature (only on LAST page) */}
      {isLast && needsSignature && (
        <div className="text-[#101010]" style={{ padding: "16px 56px 28px", fontSize: "12px" }} data-testid="doc-preview-signature">
          <div className="mb-10">Sincerely,</div>
          <div className="font-bold">{sender?.full_name}</div>
          {sender?.title && <div className="text-[#5A5A5A]">{sender.title}</div>}
          {sender?.company_name && <div className="text-[#5A5A5A]">{sender.company_name}</div>}
          <div className="text-[#5A5A5A]">
            {[sender?.phone, sender?.mobile && `Mobile ${sender.mobile}`, sender?.email].filter(Boolean).join("  ·  ")}
          </div>
          {sender?.license_number && (
            <div className="text-[#5A5A5A] mt-1" style={{ fontSize: "10px" }}>License: {sender.license_number}</div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="border-t border-[#E2E8F0] bg-[#FAFBFC] mt-auto" style={{ padding: "10px 56px" }}>
        {brand?.confidentiality_notice && (
          <div className="italic text-[#777] leading-snug mb-1" style={{ fontSize: "8px" }}>{brand.confidentiality_notice}</div>
        )}
        <div className="flex items-center justify-between text-[#444]" style={{ fontSize: "9px" }}>
          <span>{[brand?.brand_name, brand?.website, brand?.phone, brand?.email].filter(Boolean).join("  ·  ")}</span>
          <span>Page {pageNumber} of {totalPages}</span>
        </div>
      </div>
    </div>
  );
}

// Set of doc types that get a full cover page (mirrors backend _COVER_TYPES)
const COVER_TYPES = new Set([
  "loi", "purchase_proposal", "seller_financing", "master_lease",
  "investor_presentation", "property_analysis", "business_analysis",
  "due_diligence", "executive_summary", "acquisition_summary",
  "partnership_proposal",
]);

// Mirror of backend sanitize_content (post-fix ordering): preserves structural
// markdown markers (HR + table separator) so the markdown renderer can produce
// real tables/HRs, while still stripping dashes, emoji, placeholders, and
// AI artifacts from inline prose.
function sanitizeForPreview(text) {
  if (!text) return "";

  const HR_RE = /^\s*([-*_])\1{2,}\s*$/;
  const TABLE_SEP_RE = /^\s*\|?\s*:?-+:?(\s*\|\s*:?-+:?)+\s*\|?\s*$/;
  const EMOJI_RE = /[\u2600-\u27BF]|[\u{1F300}-\u{1F6FF}\u{1F900}-\u{1F9FF}\u{1FA70}-\u{1FAFF}\u{1F1E0}-\u{1F1FF}]/gu;

  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const out = [];
  for (const raw of lines) {
    if (HR_RE.test(raw) || TABLE_SEP_RE.test(raw)) {
      out.push(raw);
      continue;
    }
    let ln = raw;
    // Inline dash substitutions ,  collapse surrounding whitespace so
    // "word ,  word" becomes "word, word" rather than "word ,  word".
    ln = ln.replace(/\s*[\u2014\u2015]\s*/g, ", ");
    ln = ln.replace(/\s*[\u2013\u2012]\s*/g, " - ");
    ln = ln.replace(/\s*---\s*/g, ", ");
    ln = ln.replace(/(?<!,)\s*--\s*(?!,)/g, ", ");
    ln = ln.replace(/,\s*,\s*/g, ", ").replace(/,\s*([.;:!?])/g, "$1");
    ln = ln.replace(EMOJI_RE, "");
    out.push(ln);
  }
  let s = out.join("\n");

  const artifactPatterns = [
    /^\s*\[?(?:placeholder|insert\s+\w+(?:\s+\w+)*(?:\s+here)?|tbd|to\s*be\s*determined|fill\s*in)[^\n]*\]?\s*$/gim,
    /^\s*(?:note to (?:self|reviewer|user|editor)|internal note)\s*[:\-][^\n]*$/gim,
    /^\s*\[?\s*(?:draft|placeholder|sample|example)\s+(?:only|text)?\s*\]?\s*$/gim,
    /^\s*(?:as an ai|i am an ai|as a language model)[^\n]*$/gim,
    /^\s*\[\s*(?:end of document|begin document)\s*\]\s*$/gim,
    /^\s*[<\[]\s*[^>\]\n]{0,80}\s*[>\]]\s*$/gm,
    /^\s*_{3,}\s*$/gm,
  ];
  for (const pat of artifactPatterns) s = s.replace(pat, "");

  s = s.replace(/\[\s*(?:insert\s+[^\]]{0,40}|placeholder[^\]]{0,40}|tbd|n\/?a)\s*\]/gi, "");
  s = s.replace(/<\s*(?:insert\s+[^>]{0,40}|placeholder[^>]{0,40})\s*>/gi, "");

  s = s.replace(/\n{3,}/g, "\n\n");
  return s.trim();
}
